import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { SPEED_XFAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { useSpring, animated } from 'react-spring';
import _merge from 'lodash/merge';
import { CCFKComponentProps } from 'common/types';
import _isString from 'lodash/isString';
import _isNumber from 'lodash/isNumber';
import _delay from 'lodash/delay';
import { getContext, isAnimationDisabled } from 'common';
import { getDuration } from 'common/parse-tokens';
import { getElevationStyle } from 'components/Elevation';
import Styled, {
  SPRING_CONFIG_NUMBER_BADGE,
  SPRING_CONFIG_DOT_BADGE_NEW,
  SPRING_CONFIG_DOT_BADGE_KICK,
} from './styles';

const PADDING_WIDTH = 8; // left and right padding (px) since getTextWidth is in px
// used for number badge width animation
const getTextWidth = (text) => {
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');
  context.font = '0.625rem NokiaPureRegular';
  context.fontWeight = 700;
  context.lineHeight = 1;
  const widthPixels = Math.ceil(context.measureText(text).width);
  return widthPixels + PADDING_WIDTH;
};

interface BadgeProps extends CCFKComponentProps {
  /** If true, the badge will have a border. Applies to dot badge. */
  border?: boolean;
  /** The color for the badge border (when border=true). Should match the background color the badge is displayed upon. Applies to dot badge. */
  borderColor?: string;
  /** Set to a new value to make the dot badge grow to indicate arrival of new notifications. Applies to dot badge. */
  kick?: string | number;
  /** Control animation config for the growth of the number badge. This uses react-spring transitions config. See https://react-spring.io for more information. */
  numberBadgeAnimationProps?: {};
  /** Control animation config for a new dot badge. This uses react-spring transitions config. See https://react-spring.io for more information. */
  dotBadgeNewAnimationProps?: {};
  /** Control animation config for when the dot badge is kicked. This uses react-spring transitions config. See https://react-spring.io for more information. */
  dotBadgeUpdatedAnimationProps?: {};
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** The elevation number that defines the style of the container. */
  elevationIndex?: 0 | 1 | 2 | 3 | 4 | 6 | 8 | 9 | 12 | 16 | 24;
  /** The content of the Badge, usually a string if used. Applies to number badge. */
  children?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  style?: React.CSSProperties
}

/** Any other props than `children` will be provided to the root element. */
const Badge = React.forwardRef<HTMLElement, BadgeProps>((props: BadgeProps, ref) => getContext(({ theme, disableAnimation }) => {
  const {
    border,
    borderColor,
    children,
    style,
    kick,
    dotBadgeUpdatedAnimationProps,
    dotBadgeNewAnimationProps,
    numberBadgeAnimationProps,
    disableAnimation: propsdisableAnimation,
    elevationIndex,
    theme: propTheme,
    ...otherProps
  } = props;
  const [width, setWidth] = useState(0);
  const oldWidth = useRef(0);
  const oldKick = useRef(undefined);

  const resolvedElevation = getElevationStyle({ elevationIndex });
  const resolvedBorderColor = borderColor || resolvedElevation.backgroundColor;
  const resolvedTheme = _merge({}, theme.BADGE, propTheme.BADGE);
  const hasContent = _isString(children) || _isNumber(children);

  // animate dot growing to 120% when 'kicked'
  const [dotBadgeUpdatedAnimationKick, set] = useSpring(() => (dotBadgeUpdatedAnimationProps || {
    from: { transform: 'scale(1)' },
    config: SPRING_CONFIG_DOT_BADGE_KICK,
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
  }));

  // kick off the growth of the badge to 120% then back to 100%
  // example usage: arrival of new notifications when some already existed
  useEffect(() => {
    if (oldKick.current !== kick) {
      oldKick.current = kick;
      // @ts-expect-error: This might be fixed after updating react-spring
      set({ transform: 'scale(1.2)' });
      _delay(() => {
        // @ts-expect-error: This might be fixed after updating react-spring
        set({ transform: 'scale(1)' });
      }, getDuration(SPEED_XFAST) * 2);
    }
  }, [kick]);

  // width used in animation
  useLayoutEffect(() => {
    if (hasContent) {
      oldWidth.current = width;
      setWidth(getTextWidth(children));
    }
  }, [children, hasContent]);

  // animates increase of decrease in width
  // @ts-expect-error: This might be fixed after updating react-spring
  const numberBadgeAnimation = useSpring(numberBadgeAnimationProps || {
    from: { width: `${oldWidth.current}px` },
    to: { width: `${width}px` },
    config: SPRING_CONFIG_NUMBER_BADGE,
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation)
  });

  // animate dot growing to 120% when added
  // @ts-expect-error: This might be fixed after updating react-spring
  const dotBadgeNewAnimation = useSpring(dotBadgeNewAnimationProps || {
    from: { transform: 'scale(1.2)' },
    to: { transform: 'scale(1)' },
    leave: { transform: 'scale(1)' },
    config: SPRING_CONFIG_DOT_BADGE_NEW,
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation)
  });

  const dotAnimation = kick ? dotBadgeUpdatedAnimationKick : dotBadgeNewAnimation;
  const dotBadge = (
    <Styled.Badge
      as={animated.div}
      style={{ ...dotAnimation, ...style }}
      $theme={resolvedTheme}
      ref={ref}
      $hasContent={hasContent}
      $border={border}
      $borderColor={resolvedBorderColor}
      role="alert"
      {...otherProps}
    >
      {border && <Styled.ImageOverlay style={{ boxShadow: resolvedElevation.boxShadow }} $borderColor={resolvedBorderColor} />}
    </Styled.Badge>
  );
  // number badge animated as it grows or shrinks
  const numberBadge = (
    <Styled.Badge
      as={animated.div}
      style={{ ...numberBadgeAnimation, ...style }}
      $theme={resolvedTheme}
      ref={ref}
      $hasContent={hasContent}
      $border={border}
      $borderColor={resolvedBorderColor}
      role="alert"
      {...otherProps}
    >
      {children}
    </Styled.Badge>
  );

  return hasContent ? numberBadge : dotBadge;
}));

Badge.displayName = 'Badge';

Badge.defaultProps = {
  border: false,
  borderColor: undefined,
  kick: 0,
  dotBadgeUpdatedAnimationProps: undefined,
  numberBadgeAnimationProps: undefined,
  dotBadgeNewAnimationProps: undefined,
  disableAnimation: undefined,
  elevationIndex: 0,
  theme: {},
  children: undefined,
  style: {}
};

export { Badge as default, BadgeProps };
